import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { DataTable, type Column } from '@/components/DataTable'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { useAuth } from '@/features/auth/store'
import { listUsers } from '@/features/auth/api'
import {
  LOCATION_STATUSES,
  LOCATION_TYPES,
  LOCATION_TYPE_LABELS,
  listLocations,
  type LocationStatus,
  type LocationType,
  type StockLocation,
} from '@/features/inventory/api'
import { LocationFormDialog } from '@/features/inventory/LocationFormDialog'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<LocationStatus, string> = {
  active: 'bg-success/10 text-success',
  inactive: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
}

/**
 * Every warehouse, store and online shelf stock can sit in, and who runs it.
 *
 * No delete here -- the API has no route for it. A location that has stopped trading is
 * edited to `inactive` instead, same as `JobPostings` uses a soft-delete rather than losing
 * history, except locations do not even keep a tombstone: retiring one is just a status flip.
 */
export function Locations() {
  const can = useAuth((s) => s.can)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<LocationType | ''>('')
  const [statusFilter, setStatusFilter] = useState<LocationStatus | ''>('')
  const [dialog, setDialog] = useState<{ location?: StockLocation } | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listLocations,
    retry: false,
  })

  const canCreate = can('inventory', 'create')
  const canUpdate = can('inventory', 'update')
  const canSeeManagers = can('users', 'read')

  const users = useQuery({
    queryKey: ['users', 1],
    queryFn: () => listUsers(1),
    retry: false,
    enabled: canSeeManagers,
  })
  const managerName = (id: string | null) => {
    if (!id) return '—'
    return users.data?.data.find((user) => user.id === id)?.full_name ?? '—'
  }

  const needle = search.trim().toLowerCase()
  const locations = (data?.data ?? [])
    .filter((location) => !typeFilter || location.type === typeFilter)
    .filter((location) => !statusFilter || location.status === statusFilter)
    .filter(
      (location) =>
        !needle ||
        location.code.toLowerCase().includes(needle) ||
        location.name.toLowerCase().includes(needle),
    )

  const columns: Column<StockLocation>[] = [
    {
      key: 'code',
      header: 'Code',
      className: 'font-medium whitespace-nowrap',
      sortValue: (location) => location.code,
      cell: (location) =>
        canUpdate ? (
          <button
            type="button"
            onClick={() => setDialog({ location })}
            className="text-left hover:underline"
          >
            {location.code}
          </button>
        ) : (
          location.code
        ),
    },
    {
      key: 'name',
      header: 'Name',
      sortValue: (location) => location.name,
      cell: (location) => location.name,
    },
    {
      key: 'type',
      header: 'Type',
      cell: (location) => LOCATION_TYPE_LABELS[location.type as LocationType] ?? location.type,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (location) => (
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[location.status as LocationStatus]}`}
        >
          {location.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (location) => location.address ?? '—',
    },
    ...(canSeeManagers
      ? [
          {
            key: 'manager_id',
            header: 'Manager',
            className: 'text-slate-500 dark:text-slate-400',
            cell: (location: StockLocation) => managerName(location.manager_id),
          } satisfies Column<StockLocation>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Locations</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {data ? `${locations.length} location${locations.length === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {canCreate && <Button onClick={() => setDialog({})}>New location</Button>}
      </header>

      <Card>
        <CardHeader title="Locations" />
        <DataTable
          rows={locations}
          columns={columns}
          getRowId={(location) => location.id}
          caption="Locations"
          isLoading={isLoading}
          error={error}
          emptyMessage="No locations match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="location-search">
                Search locations
              </label>
              <input
                id="location-search"
                className={SELECT_CLASS}
                placeholder="Code or name…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />

              <label className="sr-only" htmlFor="location-type">
                Type
              </label>
              <select
                id="location-type"
                className={SELECT_CLASS}
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value as LocationType | '')}
              >
                <option value="">Any type</option>
                {LOCATION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {LOCATION_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="location-status">
                Status
              </label>
              <select
                id="location-status"
                className={SELECT_CLASS}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as LocationStatus | '')}
              >
                <option value="">Any status</option>
                {LOCATION_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status === 'active' ? 'Active' : 'Inactive'}
                  </option>
                ))}
              </select>

              {(search || typeFilter || statusFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch('')
                    setTypeFilter('')
                    setStatusFilter('')
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          }
        />
      </Card>

      {dialog && (
        <LocationFormDialog open location={dialog.location} onClose={() => setDialog(null)} />
      )}
    </div>
  )
}
